import React from 'react';
import PropTypes from 'prop-types';
import { LuBuilding, LuCheck, LuLoaderCircle, LuMap, LuUserRound } from 'react-icons/lu';
import { CARD, EmptyState, SectionHeader } from '../ProfilePage/components/ProfileUi';
import useRegionText from './regionText';

/**
 * Region manager cards, in each region's own colour. Admins see every
 * region; a region manager sees only their own (and nothing to pick when
 * there is just one — it is opened automatically).
 */
const RegionPicker = ({ isAdmin, loadingManagers, visibleViews, selectedView, onSelect }) => {
  const { label } = useRegionText();

  const header = isAdmin && (
    <SectionHeader
      icon={LuUserRound}
      gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30"
      title={label('picker.title')}
      description={label('picker.subtitle')}
    />
  );

  if (loadingManagers) {
    return (
      <section className={`${CARD} tw-space-y-4 tw-p-4 sm:tw-p-5`}>
        {header}
        <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-8 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
          <LuLoaderCircle aria-hidden="true" className="tw-h-5 tw-w-5 tw-animate-spin tw-text-teal-500" />
          {label('picker.loading')}
        </p>
      </section>
    );
  }

  if (visibleViews.length === 0) {
    return (
      <section className={`${CARD} tw-space-y-4 tw-p-4 sm:tw-p-5`}>
        {header}
        <EmptyState icon={LuMap} title={isAdmin ? label('picker.emptyAdmin') : label('picker.emptyOwn')} />
      </section>
    );
  }

  if (!isAdmin && visibleViews.length === 1) return null;

  const hasCityViews = visibleViews.some((view) => view.scope === 'city');

  return (
    <section className={`${CARD} tw-space-y-4 tw-p-4 sm:tw-p-5`}>
      {header}
      <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2 lg:tw-grid-cols-3 2xl:tw-grid-cols-4">
        {visibleViews.map((view) => {
          const selected = selectedView?.viewKey === view.viewKey;
          return (
            <button
              key={view.viewKey}
              type="button"
              onClick={() => onSelect(view)}
              aria-pressed={selected}
              // Region colour as a CSS variable: colour utilities reading it survive the dark-mode shield, inline colours don't
              style={{ '--region': view.regionColor }}
              className={`tw-relative tw-flex tw-min-w-0 tw-items-center tw-gap-3 tw-overflow-hidden tw-rounded-2xl tw-border tw-py-3.5 tw-pl-5 tw-pr-3.5 tw-text-left tw-transition focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                selected
                  ? 'tw-border-teal-500/60 tw-bg-teal-500/[0.08] tw-shadow-[0_14px_30px_-22px_rgba(13,148,136,0.7)] dark:tw-border-teal-400/50 dark:tw-bg-teal-400/10'
                  : 'tw-border-slate-200/80 tw-bg-white/70 hover:-tw-translate-y-0.5 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.06]'
              }`}
            >
              {/* Region colour strip */}
              <span aria-hidden="true" className="tw-absolute tw-inset-y-0 tw-left-0 tw-w-[5px] tw-bg-[color:var(--region)]" />
              <span className="tw-grid tw-h-11 tw-w-11 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-[color:var(--region)] tw-text-white">
                <LuUserRound aria-hidden="true" className="tw-h-5 tw-w-5" />
              </span>
              <span className="tw-min-w-0 tw-flex-1">
                <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{view.name} {view.surname}</span>
                <span className="tw-mt-0.5 tw-flex tw-min-w-0 tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-[color:var(--region)]">
                  <LuMap aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                  <span className="tw-truncate">{view.regionName}</span>
                </span>
                <span className="tw-mt-0.5 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                  <LuBuilding aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                  {label('picker.counts', { centers: view.centerCount, cities: view.cities.length })}
                </span>
                {view.email && <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{view.email}</span>}
              </span>
              {selected && (
                <span className="tw-grid tw-h-6 tw-w-6 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-teal-500 tw-text-white">
                  <LuCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
      </div>
      {hasCityViews && <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('picker.cityLegend')}</p>}
      <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('picker.toggleHint')}</p>
    </section>
  );
};

RegionPicker.propTypes = {
  isAdmin: PropTypes.bool,
  loadingManagers: PropTypes.bool,
  visibleViews: PropTypes.array.isRequired,
  selectedView: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
};

export default RegionPicker;
